import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Tooltip from 'components/rich/tooltip';
import classNames from 'classnames';
import { placeholderTextTransform } from 'editor-utils/template';

import withPlaceholderStyle from '.';
import useTextEditorContext, {
  verticalAlignment,
  lineHeight,
  textWithHtmlBreaks,
} from '../../context/textEditorContext';

const TextPlaceholder = ({ style, ...props }) => {
  const [textExceeds, setTextExceeds] = useState(false);
  const placeholderRef = useRef();
  const textRef = useRef();

  const textEditor = useTextEditorContext();

  const useTextEditor = props.interactive && textEditor;
  const useContentEditable = props.interactive && !textEditor;

  useEffect(() => {
    if (props.onTextExceeds) {
      props.onTextExceeds(textExceeds);
    }
  }, [props.onTextExceeds, textExceeds]);

  useEffect(() => {
    if (useTextEditor) {
      if (props.text.length > 0) {
        const { height: placeholderHeight } = placeholderRef.current.getBoundingClientRect();
        const { height: textHeight } = textRef.current.getBoundingClientRect();

        setTextExceeds(textHeight > placeholderHeight);
      } else {
        setTextExceeds(false);
      }
    } else if (useContentEditable) {
      if (props.text.length > 0) {
        const { height: placeholderHeight } = placeholderRef.current.getBoundingClientRect();
        const { scrollHeight: textHeight } = textRef.current;

        setTextExceeds(textHeight * props.pageSizeScale > placeholderHeight);
      } else {
        setTextExceeds(false);
      }
    }
  }, [
    props.text,
    props.placeholder,
    props.template,
    props.pageSizeScale,
    useTextEditor,
    useContentEditable,
  ]);

  if (!props.interactive && props.text.length === 0) {
    return null;
  }

  const placeholderStyle = {
    ...style,
    alignItems: verticalAlignment(props.placeholder.verticalAlignment),
    transform: `${style?.transform ?? ''} scale(${props.pageSizeScale})`,
    lineHeight: lineHeight(props.placeholder, props.template),
  };

  const textStyle = {
    textAlign: props.placeholder.alignment || 'center',
    color: props.placeholder.color,
    fontFamily: props.placeholder.fontName,
    fontSize: props.placeholder.fontSize,
  };

  let handleOnTextPlaceholderClick;
  if (useTextEditor) {
    handleOnTextPlaceholderClick = () => {
      textEditor.show({
        placeholder: props.placeholder,
        text: placeholderTextTransform(props.text, props.placeholder.textTransform),
        onSuccess: props.onChange,
      });
    };
  }

  let textElement;
  if (useContentEditable) {
    textElement = (
      <textarea
        ref={textRef}
        value={placeholderTextTransform(props.text, props.placeholder.textTransform)}
        onChange={e => props.onChange(props.placeholder, e.target.value)}
        placeholder={props.placeholder.label ? `+ ${props.placeholder.label}` : '+'}
        style={{
          ...textStyle,
        }}
      />
    );
  } else {
    let children;
    if (props.interactive && props.text.length === 0) {
      children = <>{props.placeholder.label ? `+ ${props.placeholder.label}` : '+'}</>;
    } else {
      const text = placeholderTextTransform(
        props.preview && props.text.length === 0 ? 'TXT' : props.text,
        props.placeholder.textTransform,
      );

      children = textWithHtmlBreaks(text);
    }

    textElement = (
      <span ref={textRef} style={textStyle}>
        {children}
      </span>
    );
  }

  const placeholderElement = (
    <div
      ref={placeholderRef}
      style={placeholderStyle}
      className={classNames({
        'editor-placeholder': true,
        'editor-placeholder-text': true,
        'editor-placeholder-text-interactive': useTextEditor,
        'editor-placeholder-text-empty': props.text.length === 0,
        'editor-placeholder-text-error': textExceeds,
      })}
      onClick={handleOnTextPlaceholderClick}
    >
      {textElement}
    </div>
  );

  if (textExceeds && !useContentEditable) {
    return <Tooltip text="Text is too long / large">{placeholderElement}</Tooltip>;
  }

  return placeholderElement;
};

TextPlaceholder.propTypes = {
  style: PropTypes.object.isRequired,
  placeholder: PropTypes.object.isRequired,
  page: PropTypes.object.isRequired,
  template: PropTypes.object.isRequired,
  text: PropTypes.string,
  pageSizeScale: PropTypes.number,
  interactive: PropTypes.bool,
  preview: PropTypes.bool,
  onChange: PropTypes.func,
  onTextExceeds: PropTypes.func,
};

TextPlaceholder.defaultProps = {
  text: '',
  interactive: false,
  preview: false,
  pageSizeScale: 1,
};

export default withPlaceholderStyle(TextPlaceholder, true);
